import type { ReactNode } from 'react';
import React, { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme, _params) => ({
  root: {
    width: '100%',
    margin: 'auto',
    marginTop: theme.spacing(2),
  },
  cardHeader: {
    cursor: 'pointer',
    '& .MuiCardHeader-action': {
      alignSelf: 'center',
      display: 'flex',
      alignItems: 'center',
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
    },
  },
  cardHeaderHiddenToggle: {
    cursor: 'default',
  },
  expandIconContainer: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: 0,
    '& .MuiCheckbox-root': {
      padding: 0,
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 0,
      transform: 'translateY(-1px)',
    },
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}));

interface ExpandableCardProps {
  id: string;
  title: string;
  children: ReactNode | ReactNode[];
  expandIcon?: ReactNode;
  className?: string;
  cardHeaderProps?: SxProps<Theme>;
  titleProps?: object;
  isExpanded: boolean;
  hideToggle?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const ExpandableCard: React.FC<ExpandableCardProps> = ({
  id = '',
  className,
  title,
  titleProps,
  cardHeaderProps,
  children,
  expandIcon,
  isExpanded,
  hideToggle,
  onExpandedChange,
}) => {
  const { classes, cx } = useStyles();
  const [expanded, setExpanded] = useState<boolean>(hideToggle || isExpanded);

  useEffect(() => {
    setExpanded(hideToggle || isExpanded);
  }, [hideToggle, isExpanded]);

  const handleExpandClick = () => {
    if (hideToggle || expanded) return;

    setExpanded(true);
    onExpandedChange?.(true);
    // dispatch action to clear condition
  };

  const getExpandIcon = () => {
    if (hideToggle) return <></>;

    if (expandIcon) {
      return (
        <Box className={classes.expandIconContainer} onClick={(event) => event.stopPropagation()}>
          {expandIcon}
        </Box>
      );
    }

    return (
      <IconButton
        id='expandButton'
        className={expanded ? classes.expandOpen : classes.expand}
        onClick={(event) => {
          event.stopPropagation();
          handleExpandClick();
        }}
        aria-expanded={expanded}
        aria-label='show more'
      >
        <ExpandMoreIcon />
      </IconButton>
    );
  };

  return (
    <Card raised={false} elevation={0} className={cx(classes.root, className)} id={`${id}-expandable-card`}>
      <CardHeader
        className={cx(classes.cardHeader, hideToggle && classes.cardHeaderHiddenToggle)}
        sx={cardHeaderProps}
        title={title}
        titleTypographyProps={{ ...titleProps }}
        action={getExpandIcon()}
        onClick={handleExpandClick}
      />
      <Collapse in={expanded} timeout='auto' unmountOnExit>
        <CardContent>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

export default ExpandableCard;
